/**
 * Region computation for the Plan page's Timeline canvas — see PlanRegion (types.ts) for what a
 * region represents. A region is never stored: it's derived fresh from the current set of
 * user-drawn lines (PlanDrawnLine) every time it's needed, so a card's region membership always
 * reflects the live line layout rather than a snapshot that could drift out of sync as lines are
 * added, removed, or dragged.
 *
 * The core idea is a 2D grid cut by however many vertical/horizontal lines currently exist: N
 * vertical lines split the grid into N+1 columns, M horizontal lines split it into M+1 rows, for
 * (N+1)*(M+1) regions total — matching the doubling pattern described where one vertical line
 * makes 2 regions, adding a horizontal line makes 4, adding another vertical line makes 6, etc.
 * With zero lines of an orientation, that axis has exactly one "region" spanning the full bound.
 */
import { PlanDrawnLine, PlanCardPlacement, PlanRegion } from 'types';

/** The outer edges regions are bounded by when no line exists to bound them instead — normally
 * the Timeline grid's own pannable extent (see TimelineCanvas.tsx), not the raw canvas origin,
 * since the frozen row/column are reserved chrome no card (and so no region) can ever occupy. */
export interface PlanRegionBounds {
    xMin: number;
    xMax: number;
    yMin: number;
    yMax: number;
}

/**
 * Builds every region in the current line layout. Vertical lines are sorted left-to-right and
 * used to cut the x-axis into columns; horizontal lines are sorted top-to-bottom and cut the
 * y-axis into rows. Every column/row combination produces one region, so the result always has
 * exactly (verticalLines.length + 1) * (horizontalLines.length + 1) entries.
 */
export function computeRegions(lines: PlanDrawnLine[], bounds: PlanRegionBounds): PlanRegion[] {
    const verticalLines = lines.filter((line) => line.orientation === 'vertical').slice().sort((a, b) => a.position - b.position);
    const horizontalLines = lines.filter((line) => line.orientation === 'horizontal').slice().sort((a, b) => a.position - b.position);

    const xEdges = [bounds.xMin, ...verticalLines.map((line) => line.position), bounds.xMax];
    const yEdges = [bounds.yMin, ...horizontalLines.map((line) => line.position), bounds.yMax];

    const regions: PlanRegion[] = [];
    for (let rowIndex = 0; rowIndex < yEdges.length - 1; rowIndex++) {
        for (let columnIndex = 0; columnIndex < xEdges.length - 1; columnIndex++) {
            regions.push({
                id: `${columnIndex}:${rowIndex}`,
                columnIndex,
                rowIndex,
                xMin: xEdges[columnIndex],
                xMax: xEdges[columnIndex + 1],
                yMin: yEdges[rowIndex],
                yMax: yEdges[rowIndex + 1],
                leftLine: verticalLines[columnIndex - 1] ?? null,
                rightLine: verticalLines[columnIndex] ?? null,
                topLine: horizontalLines[rowIndex - 1] ?? null,
                bottomLine: horizontalLines[rowIndex] ?? null,
            });
        }
    }
    return regions;
}

/**
 * Finds the region a point falls inside. Adjacent regions share an edge, so the lower bound of
 * each axis is always inclusive (`>=`) while the upper bound is exclusive (`<`) UNLESS there's no
 * line on that side (rightLine/bottomLine null, i.e. the region is the last one on that axis) —
 * then the upper bound is inclusive too, so a point exactly on the grid's own far edge still
 * resolves to the last region instead of falling through every region entirely.
 */
export function getRegionForPoint(x: number, y: number, regions: PlanRegion[]): PlanRegion | null {
    for (const region of regions) {
        const withinX = x >= region.xMin && (x < region.xMax || region.rightLine === null);
        const withinY = y >= region.yMin && (y < region.yMax || region.bottomLine === null);
        if (withinX && withinY) return region;
    }
    return null;
}

/** Finds the region a placed card belongs to, judged by its center point rather than its
 * top-left corner — a card straddling a line reads as belonging to whichever side holds most of
 * it, not whichever side its corner happens to sit on. */
export function getRegionForPlacement(placement: PlanCardPlacement, regions: PlanRegion[]): PlanRegion | null {
    const centerX = placement.x + placement.width / 2;
    const centerY = placement.y + placement.height / 2;
    return getRegionForPoint(centerX, centerY, regions);
}

/** Region's release position (1-based, for display) — see PlanRegion's doc comment for why
 * rowIndex doubles as this. */
export function regionReleaseLabel(region: PlanRegion): string {
    return `Release ${region.rowIndex + 1}`;
}

/** Region's priority position (1-based, for display) — see PlanRegion's doc comment for why
 * columnIndex doubles as this. */
export function regionPriorityLabel(region: PlanRegion): string {
    return `Priority ${region.columnIndex + 1}`;
}
